import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Users,
  Copy,
  CheckCircle,
  Clock,
  Share,
  Gift,
  Phone,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { getStoredUserSession } from "@shared/hooks/useUserSession";

export const Route = createFileRoute("/app/invites")({
  component: InvitesPage,
});

function InvitesPage() {
  const currentLang = getCurrentLanguage();
  const { userId, sessionToken } = getStoredUserSession();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [friendPhone, setFriendPhone] = useState("");
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  const user = useQuery(
    api.auth.getCurrentUser,
    userId ? { userId } : "skip"
  );

  const invites = useQuery(
    api.invites.getUserInvites,
    userId && sessionToken ? { userId, sessionToken } : "skip"
  );

  const inviteTree = useQuery(
    api.invites.getInviteTree,
    userId && sessionToken ? { userId, sessionToken } : "skip"
  );

  const sendPhoneInvite = useMutation(api.invites.sendPhoneInvite);

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleShare = async (code: string) => {
    const shareText = currentLang === "ro"
      ? `Alătură-te Suuper cu codul meu de invitație: ${code}\n\nDescarcă aplicația și beneficiază de reduceri exclusive!`
      : `Join Suuper with my invite code: ${code}\n\nDownload the app and enjoy exclusive discounts!`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Suuper",
          text: shareText,
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      handleCopy(code);
    }
  };

  const getErrorMessage = (error: string | undefined): string => {
    if (!error) {
      return currentLang === "ro" ? "Eroare necunoscută" : "Unknown error";
    }

    const errorMap: Record<string, { ro: string; en: string }> = {
      invalidPhoneFormat: {
        ro: "Format număr telefon invalid. Folosește formatul: 0712345678 sau +40712345678.",
        en: "Invalid phone number format. Use format: 0712345678 or +40712345678.",
      },
      phoneAlreadyRegistered: {
        ro: "Acest număr de telefon este deja înregistrat.",
        en: "This phone number is already registered.",
      },
      noInvitesRemaining: {
        ro: "Nu mai ai invitații disponibile.",
        en: "You have no invites remaining.",
      },
      noAvailableInvites: {
        ro: "Nu ai invitații disponibile.",
        en: "You have no available invites.",
      },
      userNotFound: {
        ro: "Utilizatorul nu a fost găsit.",
        en: "User not found.",
      },
    };

    const message = errorMap[error];
    if (message) {
      return currentLang === "ro" ? message.ro : message.en;
    }

    // Fallback pentru erori din common.json
    if (error === "invalidPhoneFormat") {
      return currentLang === "ro"
        ? "Format număr telefon invalid. Folosește formatul: 0712345678 sau +40712345678."
        : "Invalid phone number format. Use format: 0712345678 or +40712345678.";
    }

    return error;
  };

  const handleSendPhoneInvite = async () => {
    if (!userId || !friendPhone.trim()) return;

    setIsSendingInvite(true);
    try {
      const result = await sendPhoneInvite({
        userId,
        sessionToken: sessionToken!,
        friendPhone: friendPhone.trim(),
      });

      if (result.success) {
        setFriendPhone("");
        alert(
          currentLang === "ro"
            ? "Invitația a fost trimisă cu succes!"
            : "Invitation sent successfully!"
        );
      } else {
        const errorMessage = getErrorMessage(result.error);
        alert(
          currentLang === "ro"
            ? `Eroare: ${errorMessage}`
            : `Error: ${errorMessage}`
        );
      }
    } catch (error) {
      console.error("Error sending invite:", error);
      alert(
        currentLang === "ro"
          ? "Eroare la trimiterea invitației"
          : "Error sending invitation"
      );
    } finally {
      setIsSendingInvite(false);
    }
  };

  // Formatare dată
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString(
      currentLang === "ro" ? "ro-RO" : "en-US",
      {
        day: "numeric",
        month: "short",
        year: "numeric",
      }
    );
  };

  if (user === undefined || invites === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (user === null) {
    return null;
  }

  // Filtrează invitațiile
  const activeInvites = invites.filter(
    (i) => i.active && !i.usedBy && Date.now() < i.expiresAt
  );
  const usedInvites = invites.filter((i) => i.usedBy);

  return (
    <div className="px-4 py-6 pt-[190px] space-y-6">
        {/* Banner promoțional */}
        <div className="bg-gradient-to-r from-primary to-accent-dark rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="w-32 h-32 absolute top-0 right-0 bg-white/10 rounded-full -mr-16 -mt-16" />
          <div className="w-24 h-24 absolute bottom-0 left-0 bg-white/10 rounded-full -ml-12 -mb-12" />
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <p className="text-xl font-bold mb-2 text-white">
                  {currentLang === "ro"
                    ? "Invită 5 prieteni și primești 7 zile GRATIS!"
                    : "Invite 5 friends and get 7 days FREE!"}
                </p>
                <p className="text-white/90 text-sm">
                  {currentLang === "ro"
                    ? "Pentru fiecare prieten care se înregistrează și activează contul, primești o zi bonus"
                    : "For each friend who registers and verifies their phone, you get a bonus day"}
                </p>
              </div>
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Gift size={24} />
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">
                  {currentLang === "ro" ? "Progres" : "Progress"}
                </span>
                <span className="text-lg font-bold">
                  {user?.activatedInvitesCount || 0}/5
                </span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-white h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(((user?.activatedInvitesCount || 0) / 5) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>

            {/* Status message */}
            {user && user.activatedInvitesCount >= 5 ? (
              <div className="flex items-center gap-2 bg-white/20 rounded-lg p-3">
                <CheckCircle size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">
                  {currentLang === "ro"
                    ? "Felicitări! Ai primit 7 zile bonus!"
                    : "Congratulations! You've received 7 bonus days!"}
                </span>
              </div>
            ) : (
              <p className="text-sm text-white/80">
                {currentLang === "ro"
                  ? `${5 - (user?.activatedInvitesCount || 0)} invitații rămase pentru bonus`
                  : `${5 - (user?.activatedInvitesCount || 0)} invites remaining for bonus`}
              </p>
            )}
          </div>
        </div>

        {/* Invitare prin SMS */}
        <div className="bg-white rounded-xl p-4 border border-border">
          <h5 className="font-semibold text-text mb-3">
            {currentLang === "ro" ? "Invită prin SMS" : "Invite via SMS"}
          </h5>
          <div className="space-y-3">
            <div>
              <label
                htmlFor="friendPhone"
                className="block text-sm font-medium text-text mb-2"
              >
                {currentLang === "ro" ? "Număr telefon prieten" : "Friend's phone number"}
              </label>
              <div className="relative">
                <Phone size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  id="friendPhone"
                  type="tel"
                  value={friendPhone}
                  onChange={(e) => setFriendPhone(e.target.value)}
                  placeholder={currentLang === "ro" ? "0712345678" : "0712345678"}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                />
              </div>
            </div>
            <button
              onClick={handleSendPhoneInvite}
              disabled={!friendPhone.trim() || isSendingInvite}
              className="w-full py-3 bg-primary text-white font-semibold rounded-xl flex items-center justify-center gap-2 hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperPlaneTilt size={20} />
              {isSendingInvite
                ? currentLang === "ro"
                  ? "Se trimite..."
                  : "Sending..."
                : currentLang === "ro"
                  ? "Trimite invitația"
                  : "Send invitation"}
            </button>
            <p className="text-xs text-text-muted text-center">
              {currentLang === "ro"
                ? "Vom trimite un SMS cu codul de invitație. După ce prietenul se înregistrează și și iși face cont cu acelasi număr de telefon, trial-ul tău se va debloca automat."
                : "We'll send an SMS with the invite code. After your friend registers and verifies their phone, your trial will unlock automatically."}
            </p>
          </div>
        </div>

        {/* Active invites */}
        {activeInvites.length > 0 && (
          <div>
            <h5 className="font-semibold text-text mb-3">
              {currentLang === "ro" ? "Coduri disponibile" : "Available codes"}
            </h5>
            <div className="space-y-2">
              {activeInvites.map((invite) => (
                <div
                  key={invite._id}
                  className="bg-white rounded-xl p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="font-mono font-bold text-lg text-text">
                      {invite.code}
                    </p>
                    <p className="text-xs text-text-muted flex items-center gap-1">
                      <Clock size={3} />
                      {currentLang === "ro" ? "Expiră" : "Expires"}{" "}
                      {formatDate(invite.expiresAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleCopy(invite.code)}
                      className={`p-2 rounded-lg transition-colors ${
                        copiedCode === invite.code
                          ? "bg-success/10 text-success"
                          : "bg-gray-100 text-text-muted hover:bg-gray-200"
                      }`}
                    >
                      {copiedCode === invite.code ? (
                        <CheckCircle size={20} />
                      ) : (
                        <Copy size={20} />
                      )}
                    </button>
                    <button
                      onClick={() => handleShare(invite.code)}
                      className="p-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20"
                    >
                      <Share size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invited users */}
        {inviteTree && inviteTree.length > 0 && (
          <div>
            <h5 className="font-semibold text-text mb-3">
              {currentLang === "ro" ? "Prieteni invitați" : "Invited friends"}
            </h5>
            <div className="space-y-2">
              {inviteTree.map((invited) => (
                <div
                  key={invited._id}
                  className="bg-white rounded-xl p-4 flex items-center gap-3"
                >
                  <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                    <CheckCircle size={20} className="text-success" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text truncate">
                      {invited.email}
                    </p>
                    <p className="text-xs text-text-muted">
                      {formatDate(invited.createdAt)}
                      {invited.invitedCount > 0 && (
                        <span className="ml-2">
                          • {invited.invitedCount}{" "}
                          {currentLang === "ro" ? "invitați" : "invited"}
                        </span>
                      )}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      invited.membershipStatus === "active"
                        ? "bg-success/10 text-success"
                        : invited.membershipStatus === "trial"
                          ? "bg-accent/20 text-accent-dark"
                          : "bg-gray-100 text-text-muted"
                    }`}
                  >
                    {invited.membershipStatus === "active"
                      ? currentLang === "ro"
                        ? "Activ"
                        : "Active"
                      : invited.membershipStatus === "trial"
                        ? "Trial"
                        : currentLang === "ro"
                          ? "Expirat"
                          : "Expired"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Used invites */}
        {usedInvites.length > 0 && (
          <div>
            <h5 className="font-semibold text-text mb-3">
              {currentLang === "ro" ? "Coduri folosite" : "Used codes"}
            </h5>
            <div className="space-y-2">
              {usedInvites.map((invite) => (
                <div
                  key={invite._id}
                  className="bg-white rounded-xl p-4 flex items-center justify-between opacity-60"
                >
                  <div>
                    <p className="font-mono text-text line-through">
                      {invite.code}
                    </p>
                    <p className="text-xs text-text-muted">
                      {currentLang === "ro" ? "Folosit pe" : "Used on"}{" "}
                      {formatDate(invite.usedAt!)}
                    </p>
                  </div>
                  <CheckCircle size={20} className="text-success" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {activeInvites.length === 0 && usedInvites.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users size={24} className="text-text-muted" />
            </div>
            <h5 className="text-lg font-semibold text-text mb-2">
              {currentLang === "ro" ? "Nicio invitație" : "No invitations"}
            </h5>
            <p className="text-text-muted">
              {currentLang === "ro"
                ? "Nu ai coduri de invitație disponibile momentan"
                : "You don't have any invitation codes available"}
            </p>
          </div>
        )}
    </div>
  );
}
